export const VISUAL_MENTION_PLACEMENT_TYPES = [
  "physical_placement",
  "banner",
  "overlay",
] as const;

export type VisualMentionPlacementType =
  (typeof VISUAL_MENTION_PLACEMENT_TYPES)[number];

export function isVisualMentionPlacement(
  placementType?: string,
): placementType is VisualMentionPlacementType {
  return VISUAL_MENTION_PLACEMENT_TYPES.includes(
    placementType as VisualMentionPlacementType,
  );
}

function getVisualPlacementPositionLabel(
  placementType: VisualMentionPlacementType,
  mention?: { visualCue?: string },
): string {
  if (placementType === "banner") {
    if (mention?.visualCue === "lateral_banner") {
      return "Banner lateral";
    }
    return "Banner";
  }
  if (placementType === "overlay") {
    return "Overlay";
  }
  return "Placement";
}

/**
 * Credits placements have a fixed position regardless of segmentStart, which is important for
 * duplicated/credits rows whose segmentStart is a placeholder (often 0) and would otherwise all
 * bucket to Pre-roll. Returns the credits label, or null to fall back to time-based logic.
 */
function getCreditsPlacementLabel(
  placementType: string | undefined,
): string | null {
  if (placementType === "opening_credits") {
    return "Créditos de apertura";
  }
  if (placementType === "mid_credits") {
    return "Créditos intermedios";
  }
  if (placementType === "closing_credits") {
    return "Créditos de cierre";
  }
  return null;
}

/** Posición label for spoken mentions (Pre/Mid/Post-roll) vs visual placements. */
export function getMentionPositionLabel(
  mention: {
    placementType?: string;
    segmentStart?: number | null;
    visualCue?: string;
  },
  content?: { duration?: number },
): string {
  if (isVisualMentionPlacement(mention.placementType)) {
    return getVisualPlacementPositionLabel(
      mention.placementType,
      mention,
    );
  }

  const creditsLabel = getCreditsPlacementLabel(mention.placementType);
  if (creditsLabel) return creditsLabel;

  const startTime = mention.segmentStart;
  const duration = content?.duration;
  if (
    startTime == null ||
    duration == null ||
    duration <= 0 ||
    Number.isNaN(startTime)
  ) {
    return "No especificada";
  }

  if (startTime < duration * 0.33) return "Pre-roll";
  if (startTime < duration * 0.66) return "Mid-roll";
  return "Post-roll";
}

/** PDF/export alias — same plain labels as UI tables. */
export function getMentionPositionLabelPdf(
  mention: {
    placementType?: string;
    segmentStart?: number | null;
    visualCue?: string;
  },
  content?: { duration?: number },
): string {
  return getMentionPositionLabel(mention, content);
}
